import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import ProductCartCounter from '../components/ProductCartCounter';
import CombinedCheckoutModal from '../components/CombinedCheckoutModal';
import { useHairSession } from '../context/HairSessionContext';
import { isPresentationMode, navigateHairFlowHome } from '../utils/hairSalonFlowDeps';

import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { getSessionIdFromUrl, getSessionId, pathWithSessionId } from '../utils/sessionManager';
import { saveSkinFlowState, getSkinFlowState } from '../utils/skinFlowState';
import { getApiBaseUrl, Hair_API_PREFIX } from '../utils/apiConfig';
import { sanitizeHtml, stripHtml } from '../utils/sanitizeHtml';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import '../styles/PageBackground.css';
import './ProductRecommendationsPage.css';

const DEFAULT_PRODUCT_SUGGEST_LIMIT = 4;
const PRODUCT_RECOMMENDATIONS_CACHE_PREFIX = 'Hair_product_recommendations_';



const getScoreDisplayLabel = (score) => {
  if (score == null || typeof score !== 'number') return null;
  const n = Math.round(Number(score));
  if (n >= 0 && n <= 39) return 'Critical';
  if (n >= 40 && n <= 59) return 'Moderate';
  if (n >= 60 && n <= 74) return 'Fair';
  if (n >= 75 && n <= 89) return 'Good';
  return 'Excellent';
};

const normalizeWhyRecommendedText = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/\(score:\s*(\d+)\)/gi, (_, num) => {
    const label = getScoreDisplayLabel(parseInt(num, 10));
    return label != null ? `(${label})` : '';
  });
};

const ProductRecommendationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, sessionLandingPath } = useHairSession();


  const sessionIdFromState = location.state?.sessionId || null;
  const sessionIdFromUrl = getSessionIdFromUrl(location);
  const sessionIdFromStorage = getSessionId();
  const sessionId = sessionIdFromState || sessionIdFromUrl || sessionIdFromStorage || null;

  const effectiveState = useMemo(() => {
    if (
      location.state &&
      (location.state.hairResults || location.state.routineData || location.state.recommendations || location.state.treatmentCartItems != null)

    ) {
      return location.state;
    }
    if (!sessionId) return null;
    return getSkinFlowState(sessionId);
  }, [location.state, sessionId]);

  const hairResultsFromState = effectiveState?.hairResults || location.state?.hairResults || null;
  const cartItemsFromState = effectiveState?.cartItems ?? location.state?.cartItems ?? null;
  const treatmentCartItemsFromState = effectiveState?.treatmentCartItems ?? location.state?.treatmentCartItems ?? null;
  const updatedCartFromCheckout = location.state?.updatedCartFromCheckout || false;
  const [cartItems, setCartItems] = useState(cartItemsFromState || location.state?.cartItems || {}); // Store cart items: { productId: { product, quantity } });
  const [treatmentCartItems, setTreatmentCartItems] = useState(
    () =>
      treatmentCartItemsFromState && typeof treatmentCartItemsFromState === 'object'
        ? treatmentCartItemsFromState
        : {}
  );

  // Persist full state (including cartItems) so it survives refresh
  useEffect(() => {
    if (!sessionId) return;
    const current = getSkinFlowState(sessionId) || {};
    const fromLocation = location.state && typeof location.state === 'object' ? location.state : {};
    const merged = {
      ...current,
      ...fromLocation,
      sessionId,
      cartItems,
      treatmentCartItems,
    };
    saveSkinFlowState(sessionId, merged);
  }, [sessionId, cartItems, treatmentCartItems, location.state]);
  const [recommendations, setRecommendations] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('morning');
  const [uspPopup, setUspPopup] = useState({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  // Product suggestion config from session app-data (suggest-product returns flags + counts)
  const [productSuggestLimit, setProductSuggestLimit] = useState(null);
  const [productSuggestionEnabled, setProductSuggestionEnabled] = useState(true);
  const [morningCount, setMorningCount] = useState(null);
  const [eveningCount, setEveningCount] = useState(null);

  useEffect(() => {
    if (!token) {
      setProductSuggestLimit(null);
      setProductSuggestionEnabled(true);
      setMorningCount(null);
      setEveningCount(null);
      return;
    }
    let cancelled = false;
    fetchWithTimeout(`${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/suggest-product`, { timeoutMs: 20000 })
      .then((res) => {
        if (!res.ok) throw new Error('Suggest product config unavailable');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (isPresentationMode()) {
          setProductSuggestionEnabled(true);
          const limit = data?.product_suggest_limit;
          const num = typeof limit === 'number' ? limit : parseInt(limit, 10);
          setProductSuggestLimit(Number.isNaN(num) || num < 1 ? DEFAULT_PRODUCT_SUGGEST_LIMIT : num);
          const mCount = Number.isNaN(Number(data?.morning_count)) ? null : Number(data?.morning_count);
          const eCount = Number.isNaN(Number(data?.evening_count)) ? null : Number(data?.evening_count);
          setMorningCount(mCount != null && mCount > 0 ? mCount : null);
          setEveningCount(eCount != null && eCount > 0 ? eCount : null);
          return;
        }
        const product = String(data?.product_suggestion || '').trim().toLowerCase();
        const mCount = Number.isNaN(Number(data?.morning_count)) ? null : Number(data?.morning_count);
        const eCount = Number.isNaN(Number(data?.evening_count)) ? null : Number(data?.evening_count);
        setProductSuggestionEnabled(product === 'yes');
        setMorningCount(mCount != null && mCount > 0 ? mCount : null);
        setEveningCount(eCount != null && eCount > 0 ? eCount : null);

        const limit = data?.product_suggest_limit;
        const num = typeof limit === 'number' ? limit : parseInt(limit, 10);
        setProductSuggestLimit(Number.isNaN(num) || num < 1 ? null : num);
      })
      .catch(() => {
        if (!cancelled) {
          setProductSuggestLimit(null);
          setProductSuggestionEnabled(true);
          setMorningCount(null);
          setEveningCount(null);
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  // Restore cart state from navigation state when coming back from checkout
  useEffect(() => {
    if (updatedCartFromCheckout && cartItemsFromState) {
      setCartItems(cartItemsFromState);
    }
  }, [cartItemsFromState, updatedCartFromCheckout]);

  const API_BASE_URL = getApiBaseUrl() || process.env.REACT_APP_API_URL || '';

  const isVercelBlobUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    try {
      const u = new URL(url);
      const host = (u.hostname || '').toLowerCase();
      return (
        u.protocol === 'https:' &&
        (host === 'blob.vercel-storage.com' ||
          host.endsWith('.blob.vercel-storage.com') ||
          host === 'public.blob.vercel-storage.com')
      );
    } catch {
      return false;
    }
  };

  const resolveProductImageUrl = (url) => {
    if (!url) return url;
    if (!isVercelBlobUrl(url)) return url;
    const base = (API_BASE_URL || '').replace(/\/$/, '');
    return `${base}${Hair_API_PREFIX}/blob-proxy?url=${encodeURIComponent(url)}`;
  };

  const toNumberOrNull = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };


  const hasProductDiscount = (p) => {
    const d = toNumberOrNull(p?.discount);
    const net = toNumberOrNull(p?.amount);
    const sale = toNumberOrNull(p?.sale_price);
    if (net == null) return false;
    if (d != null && d > 0 && sale != null && sale > 0 && sale < net) return true;
    if (net != null && sale != null && sale > 0 && sale < net) return true;
    return false;
  };

  const hasProductNetPrice = (p) => toNumberOrNull(p?.amount) != null;
  const hasProductAmount = (p) => toNumberOrNull(p?.amount) != null;

  const formatDiscountBadge = (p) => {
    const d = Number(p?.discount);
    if (Number.isNaN(d) || d <= 0) return null;
    if (d > 0 && d <= 1) return `${Math.round(d * 100)}% OFF`;
    if (d > 1 && d <= 100) return `${Math.round(d)}% OFF`;
    return `₹${Math.round(d)} OFF`;
  };
  const isDefaultDummyProduct = (p) => {
    if (!p) return false;
    const name = (p.product_name || p.name || '').toLowerCase();
    return name.includes('scalp revitalizing') || name.includes('anti-frizz') || name.includes('silk oil');
  };



  useEffect(() => {
    if (!sessionId) return;
    const fromState = effectiveState?.recommendations ?? location.state?.recommendations ?? null;
    if (fromState && typeof fromState === 'object') {
      const morning = (fromState.recommended_products?.morning || []).filter(p => !isDefaultDummyProduct(p));
      const evening = (fromState.recommended_products?.evening || []).filter(p => !isDefaultDummyProduct(p));
      if (morning.length > 0 || evening.length > 0) {
        setRecommendations({ ...fromState, recommended_products: { morning, evening } });
        setIsLoading(false);
        return;
      }
    }
    try {
      const cached = localStorage.getItem(`${PRODUCT_RECOMMENDATIONS_CACHE_PREFIX}${sessionId}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object') {
          const morning = (parsed.recommended_products?.morning || []).filter(p => !isDefaultDummyProduct(p));
          const evening = (parsed.recommended_products?.evening || []).filter(p => !isDefaultDummyProduct(p));
          if (morning.length > 0 || evening.length > 0) {
            setRecommendations({ ...parsed, recommended_products: { morning, evening } });
            setIsLoading(false);
            return;
          }
        }
      }
    } catch (e) {
      // ignore cache read issues
    }
  }, [sessionId, effectiveState?.recommendations, location.state?.recommendations]);

  const handleAddToCart = (product, quantity, productId) => {
    // Update cart state with product ID as key, storing both product data and quantity
    setCartItems(prev => {
      if (quantity === 0) {
        // Remove item from cart if quantity is 0
        const newCart = { ...prev };
        delete newCart[productId];
        return newCart;
      }
      return {
        ...prev,
        [productId]: {
          product: product,
          quantity: quantity
        }
      };
    });
  };

  const handleCheckout = () => {
    setIsCheckoutModalOpen(true);
  };

  const handleCloseCheckoutModal = () => {
    setIsCheckoutModalOpen(false);
  };

  const handleCartUpdate = (updatedCartItems) => {
    setCartItems(updatedCartItems);
  };

  const handleTreatmentCartUpdate = (updatedTreatmentCartItems) => {
    setTreatmentCartItems(updatedTreatmentCartItems || {});
  };

  const getTotalTreatmentCartItems = () =>
    Object.values(treatmentCartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const getTotalCombinedCartItems = () => getTotalCartItems() + getTotalTreatmentCartItems();

  // Persist cart to backend whenever cart changes (Add to Cart, +/- quantity, or modal update)
  const saveCartToBackend = useCallback((items) => {
    const baseUrl = (API_BASE_URL || '').replace(/\/$/, '');
    if (!baseUrl || !sessionId) return;
    const cartProducts = Object.entries(items)
      .filter(([, item]) => item && item.quantity > 0)
      .map(([, item]) => ({ product: item.product, quantity: item.quantity }));
    fetchWithTimeout(`${baseUrl}${Hair_API_PREFIX}/save-cart-products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        skin_id: sessionId,
        session_id: sessionId,
        ...(token && { app_session_token: token }),
        cart_products: cartProducts
      }),
      timeoutMs: 20000,
    }).then((r) => {
      if (!r.ok) console.warn('Could not save cart to backend');
    }).catch(() => { });
  }, [API_BASE_URL, sessionId, token]);

  useEffect(() => {
    saveCartToBackend(cartItems);
  }, [cartItems, saveCartToBackend]);

  // Calculate total items in cart
  const getTotalCartItems = () => {
    return Object.values(cartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  // Recommended products depend on suggest-product config and counts.

  useEffect(() => {
    if (!sessionId) return;
    setIsLoading(true);

    const baseUrl = (API_BASE_URL || '').replace(/\/$/, '');
    fetchWithTimeout(`${baseUrl}${Hair_API_PREFIX}/get-stored-routine/${sessionId}`, { timeoutMs: 15000 })
      .then(res => res.json())
      .then(data => {
        if (data && data.success && data.recommended_products) {
          const morning = (data.recommended_products.morning || []).filter(p => !isDefaultDummyProduct(p));
          const evening = (data.recommended_products.evening || []).filter(p => !isDefaultDummyProduct(p));
          const cleaned = { recommended_products: { morning, evening } };
          setRecommendations(cleaned);
          try {
            localStorage.setItem(`${PRODUCT_RECOMMENDATIONS_CACHE_PREFIX}${sessionId}`, JSON.stringify(cleaned));
          } catch (e) { }
        } else {
          setRecommendations({ recommended_products: { morning: [], evening: [] } });
        }
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Failed to fetch product recommendations:", err);
        setRecommendations({ recommended_products: { morning: [], evening: [] } });
        setIsLoading(false);
      });
  }, [sessionId, API_BASE_URL]);



  const handleUspClick = (e, uspText, heading = 'Details') => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate popup position - try to center it over the clicked element
    let left = rect.left + window.scrollX;
    let top = rect.bottom + window.scrollY + 8; // Position below the element with small gap

    // Adjust if popup would go off-screen
    const popupWidth = Math.max(rect.width, 300);
    if (left + popupWidth > viewportWidth + window.scrollX) {
      left = viewportWidth + window.scrollX - popupWidth - 16; // 16px margin
    }
    if (left < window.scrollX) {
      left = window.scrollX + 16; // 16px margin
    }

    // If popup would go below viewport, position it above instead
    if (top + 200 > viewportHeight + window.scrollY) {
      top = rect.top + window.scrollY - 200 - 8; // Position above with gap
    }

    setUspPopup({
      open: true,
      text: uspText,
      heading,
      position: {
        top,
        left,
        width: popupWidth,
      },
    });
  };

  const handleCloseUspPopup = () => {
    setUspPopup({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (uspPopup.open && !e.target.closest('.product-usp-popup') && !e.target.closest('.product-usp')) {
        handleCloseUspPopup();
      }
    };

    if (uspPopup.open) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [uspPopup.open]);

  const handleBack = () => navigate(pathWithSessionId('/hair-report', sessionId), { state: location.state });

  const handleHome = () => {
    navigateHairFlowHome(navigate, sessionLandingPath);
  };

  return (
    <div className="product-recommendations-page-shell page-bg">
      <div className="product-recommendations-page-nav">
        <button
          type="button"
          className="product-recommendations-page-back-button"
          onClick={handleBack}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="product-recommendations-page-title-wrapper">
          <h3 className="product-recommendations-page-title">
            Recommended Products
          </h3>
          <button
            type="button"
            className="product-recommendations-page-home-button"
            onClick={handleHome}
            aria-label="Go to home"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </button>
          {getTotalCombinedCartItems() > 0 && (
            <button
              type="button"
              className="product-recommendations-page-checkout-button-icon"
              onClick={handleCheckout}
              title={`Checkout (${getTotalCombinedCartItems()} items)`}
              aria-label={`Checkout with ${getTotalCombinedCartItems()} items`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              {getTotalCombinedCartItems() > 0 && (
                <span className="product-recommendations-page-checkout-badge-icon">
                  {getTotalCombinedCartItems()}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
      {!productSuggestionEnabled ? (
        <main className="product-recommendations-page-main">
          <div className="product-recommendations-page-container">
            <div className="product-recommendations-page-error">
              <p>Product recommendations are not enabled for this session.</p>
              <button type="button" className="skin-action-button" onClick={handleBack}>
                Back
              </button>
            </div>
          </div>
        </main>
      ) : (
        <main className="product-recommendations-page-main">
          <div className="product-recommendations-page-container">
            <section className="product-recommendations-page-layout">

              {isLoading ? (
                <div className="product-recommendations-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading personalized recommendations...</p>
                </div>
              ) : recommendations ? (
                <>
                  {(() => {
                    const baseLimit = (typeof productSuggestLimit === 'number' && productSuggestLimit >= 1) ? productSuggestLimit : null;
                    const finalMorningLimit = (typeof morningCount === 'number' && morningCount > 0) ? morningCount : (baseLimit || DEFAULT_PRODUCT_SUGGEST_LIMIT);
                    const finalEveningLimit = (typeof eveningCount === 'number' && eveningCount > 0) ? eveningCount : (baseLimit || DEFAULT_PRODUCT_SUGGEST_LIMIT);
                    const morningList = (recommendations.recommended_products.morning || []).slice(0, finalMorningLimit);
                    const eveningList = (recommendations.recommended_products.evening || []).slice(0, finalEveningLimit);
                    const conditionScores = recommendations.condition_scores || hairResultsFromState?.hairScore?.condition_scores || {};

                    const getRecommendationLine = (product) => {
                      if (product.suggested_for_wording) return normalizeWhyRecommendedText(product.suggested_for_wording);
                      const conditionsToShow = (product.suggested_for_conditions && product.suggested_for_conditions.length > 0)
                        ? product.suggested_for_conditions
                        : (product.concerns_treated || []);
                      if (!conditionsToShow.length) return '';
                      const findScore = (c) => {
                        if (conditionScores[c] != null) return Math.round(Number(conditionScores[c]));
                        const key = Object.keys(conditionScores).find(k => k && (k === c || k.toLowerCase() === (c && c.toLowerCase())));
                        return key != null ? Math.round(Number(conditionScores[key])) : null;
                      };
                      const parts = conditionsToShow.map(c => {
                        const score = findScore(c);
                        const label = score != null ? getScoreDisplayLabel(score) : null;
                        return label != null ? `${c} (${label})` : c;
                      });
                      return 'Recommended for ' + parts.join(', ');
                    };
                    return (
                      <div className="product-recommendations-content">
                        <div className="product-tabs">
                          <button
                            type="button"
                            className={`product-tab ${activeTab === 'morning' ? 'active' : ''}`}
                            onClick={() => setActiveTab('morning')}
                          >
                            <img
                              src={`${process.env.PUBLIC_URL}/icon/mor.png`}
                              alt="Morning"
                              className="product-tab-icon"
                            />
                            Morning ({morningList.length})
                          </button>
                          <button
                            type="button"
                            className={`product-tab ${activeTab === 'evening' ? 'active' : ''}`}
                            onClick={() => setActiveTab('evening')}
                          >
                            <img
                              src={`${process.env.PUBLIC_URL}/icon/eve.png`}
                              alt="Evening"
                              className="product-tab-icon"
                            />
                            Evening ({eveningList.length})
                          </button>
                        </div>

                        <div className="product-tab-panels">
                          {activeTab === 'morning' && (
                            <div className={`product-tab-panel ${morningList.length > 0 ? 'active' : ''}`}>
                              {morningList.length > 0 ? (
                                <div className="product-grid">
                                  {morningList.map((product, idx) => {
                                    const productId = product.product_id || product.id || `morning_${product.product_name}_${idx}`;
                                    const cartQuantity = cartItems[productId]?.quantity || 0;
                                    const showProductPrice = hasProductNetPrice(product);
                                    const showProductQuantity = hasProductAmount(product);
                                    const qtyForPrice = showProductQuantity ? Math.max(1, cartQuantity || 0) : 1;
                                    const discountBadge = showProductPrice ? formatDiscountBadge(product) : null;
                                    return (
                                      <div key={productId} className="product-card">
                                        {discountBadge ? (
                                          <div className="product-discount-badge" aria-label={`Discount ${discountBadge}`}>
                                            {discountBadge}
                                          </div>
                                        ) : null}
                                        <div className="product-card-image">
                                          {(product.image_url || product.image) ? (
                                            <img
                                              src={resolveProductImageUrl(product.image_url || product.image)}
                                              alt={product.product_name}
                                              onError={(e) => {
                                                e.target.src = "https://images.unsplash.com/photo-1526947425960-945c6e72858f?auto=format&fit=crop&w=300&q=80";
                                              }}
                                            />
                                          ) : null}
                                        </div>
                                        <div className="product-info">
                                          <div className="product-name-header">
                                            <h3 className="product-name">{product.product_name}</h3>
                                            <p style={{ color: '#db2777', fontSize: '0.8rem', fontWeight: 'bold', margin: '4px 0' }}>Recommended for: {product.hair_problem || "General Care"}</p>
                                            {(product.description || product.major_usp) && (
                                              <button
                                                type="button"
                                                className="product-usp-info-icon"
                                                onClick={(e) => handleUspClick(e, product.description || product.major_usp || 'No description available.', 'Product details')}
                                                title="View product details"
                                                aria-label="View product details"
                                              >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                  <circle cx="12" cy="12" r="10" />
                                                  <circle cx="12" cy="9.5" r="1.5" fill="currentColor" stroke="none" />
                                                  <line x1="12" y1="13.5" x2="12" y2="17" />
                                                </svg>
                                              </button>
                                            )}
                                          </div>
                                          {(() => {
                                            const recommendationLine = getRecommendationLine(product);
                                            return recommendationLine ? (
                                              <div className="product-suggested-detail-wrapper">
                                                <span className="product-card-usp-heading">Why recommended</span>
                                                <button
                                                  type="button"
                                                  className="product-suggested-detail product-usp"
                                                  onClick={(e) => handleUspClick(e, recommendationLine, 'Why recommended')}
                                                  title="View full"
                                                >
                                                  <span className="product-suggested-detail-text">{recommendationLine}</span>
                                                </button>
                                                {recommendationLine.length > 80 ? (
                                                  <button
                                                    type="button"
                                                    className="product-suggested-detail-view-full"
                                                    onClick={(e) => handleUspClick(e, recommendationLine, 'Why recommended')}
                                                  >
                                                    View full
                                                  </button>
                                                ) : null}
                                              </div>
                                            ) : null;
                                          })()}
                                          {product.major_usp && (() => {
                                            const majorUspPlain = stripHtml(product.major_usp);
                                            const isLong = majorUspPlain.length > 80;
                                            return (
                                              <div className="product-major-usp-wrapper">
                                                <span className="product-card-usp-heading">Key benefit</span>
                                                {isLong ? (
                                                  <button
                                                    type="button"
                                                    className="product-major-usp product-usp"
                                                    onClick={(e) => handleUspClick(e, product.major_usp, 'Key benefit')}
                                                    title="View full"
                                                  >
                                                    <span className="product-major-usp-text">{majorUspPlain}</span>
                                                  </button>
                                                ) : (
                                                  <span className="product-major-usp-text">{majorUspPlain}</span>
                                                )}
                                              </div>
                                            );
                                          })()}
                                          {product.concerns_treated && product.concerns_treated.length > 0 && (
                                            <div className="product-info-section">
                                              <div className="product-info-label">
                                                <img
                                                  src={`${process.env.PUBLIC_URL}/icon/treat.png`}
                                                  alt="Treats"
                                                  className="product-info-icon"
                                                />
                                                <span>Treats</span>
                                              </div>
                                              <div className="product-tags-container">
                                                {product.concerns_treated.map((concern, i) => (
                                                  <span key={i} className="product-tag">{concern}</span>
                                                ))}
                                              </div>
                                            </div>
                                          )}

                                          {product.skin_goals_supported && product.skin_goals_supported.length > 0 && (
                                            <div className="product-info-section">
                                              <div className="product-info-label">
                                                <img
                                                  src={`${process.env.PUBLIC_URL}/icon/goal.png`}
                                                  alt="Goals"
                                                  className="product-info-icon"
                                                />
                                                <span>Goals</span>
                                              </div>
                                              <div className="product-tags-container">
                                                {product.skin_goals_supported.map((goal, i) => (
                                                  <span key={i} className="product-tag">{goal}</span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                        <div className="product-card-footer">
                                          {showProductPrice ? (
                                            <div className="product-price-quantity">
                                              {(() => {
                                                const unitNet = toNumberOrNull(product.amount) ?? 0;
                                                const unitSale = toNumberOrNull(product.sale_price) ?? unitNet;
                                                const totalNet = unitNet * qtyForPrice;
                                                const totalSale = unitSale * qtyForPrice;
                                                if (hasProductDiscount(product) && totalSale > 0 && totalSale < totalNet) {
                                                  return (
                                                    <>
                                                      <span className="product-price product-net-price-slash">{`₹${totalNet.toFixed(0)}`}</span>
                                                      <span className="product-price product-sale-price">{`₹${totalSale.toFixed(0)}`}</span>
                                                    </>
                                                  );
                                                }
                                                return (
                                                  <span className="product-price">{`₹${totalNet.toFixed(0)}`}</span>
                                                );
                                              })()}
                                            </div>
                                          ) : null}
                                          {showProductQuantity ? (
                                            <ProductCartCounter
                                              key={`counter-${productId}`}
                                              product={product}
                                              onAddToCart={(product, quantity) => handleAddToCart(product, quantity, productId)}
                                              disabled={!product.add_to_cart}
                                              initialQuantity={cartQuantity}
                                            />
                                          ) : (
                                            <button
                                              type="button"
                                              className={`skin-action-button add-to-cart-button ${cartQuantity > 0 ? 'request-selected' : ''}`}
                                              onClick={() => handleAddToCart(product, cartQuantity > 0 ? 0 : 1, productId)}
                                            >
                                              {cartQuantity > 0 ? 'Selected for Stock' : 'Add to Stock Request'}
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="no-products-message">
                                  <p>No morning products found.</p>
                                </div>
                              )}
                            </div>
                          )}

                          {activeTab === 'evening' && (
                            <div className={`product-tab-panel ${eveningList.length > 0 ? 'active' : ''}`}>
                              {eveningList.length > 0 ? (
                                <div className="product-grid">
                                  {eveningList.map((product, idx) => {
                                    const productId = product.product_id || product.id || `evening_${product.product_name}_${idx}`;
                                    const cartQuantity = cartItems[productId]?.quantity || 0;
                                    const showProductPrice = hasProductNetPrice(product);
                                    const showProductQuantity = hasProductAmount(product);
                                    const qtyForPrice = showProductQuantity ? Math.max(1, cartQuantity || 0) : 1;
                                    const discountBadge = showProductPrice ? formatDiscountBadge(product) : null;
                                    return (
                                      <div key={productId} className="product-card">
                                        {discountBadge ? (
                                          <div className="product-discount-badge" aria-label={`Discount ${discountBadge}`}>
                                            {discountBadge}
                                          </div>
                                        ) : null}
                                        <div className="product-card-image">
                                          {(product.image_url || product.image) ? (
                                            <img
                                              src={resolveProductImageUrl(product.image_url || product.image)}
                                              alt={product.product_name}
                                              onError={(e) => {
                                                e.target.src = "https://images.unsplash.com/photo-1526947425960-945c6e72858f?auto=format&fit=crop&w=300&q=80";
                                              }}
                                            />
                                          ) : null}
                                        </div>
                                        <div className="product-info">
                                          <div className="product-name-header">
                                            <h3 className="product-name">{product.product_name}</h3>
                                            <p style={{ color: '#db2777', fontSize: '0.8rem', fontWeight: 'bold', margin: '4px 0' }}>Recommended for: {product.hair_problem || "General Care"}</p>
                                            {(product.description || product.major_usp) && (
                                              <button
                                                type="button"
                                                className="product-usp-info-icon"
                                                onClick={(e) => handleUspClick(e, product.description || product.major_usp || 'No description available.', 'Product details')}
                                                title="View product details"
                                                aria-label="View product details"
                                              >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                  <circle cx="12" cy="12" r="10" />
                                                  <circle cx="12" cy="9.5" r="1.5" fill="currentColor" stroke="none" />
                                                  <line x1="12" y1="13.5" x2="12" y2="17" />
                                                </svg>
                                              </button>
                                            )}
                                          </div>
                                          {(() => {
                                            const recommendationLine = getRecommendationLine(product);
                                            return recommendationLine ? (
                                              <div className="product-suggested-detail-wrapper">
                                                <span className="product-card-usp-heading">Why recommended</span>
                                                <button
                                                  type="button"
                                                  className="product-suggested-detail product-usp"
                                                  onClick={(e) => handleUspClick(e, recommendationLine, 'Why recommended')}
                                                  title="View full"
                                                >
                                                  <span className="product-suggested-detail-text">{recommendationLine}</span>
                                                </button>
                                                {recommendationLine.length > 80 ? (
                                                  <button
                                                    type="button"
                                                    className="product-suggested-detail-view-full"
                                                    onClick={(e) => handleUspClick(e, recommendationLine, 'Why recommended')}
                                                  >
                                                    View full
                                                  </button>
                                                ) : null}
                                              </div>
                                            ) : null;
                                          })()}
                                          {product.major_usp && (() => {
                                            const majorUspPlain = stripHtml(product.major_usp);
                                            const isLong = majorUspPlain.length > 80;
                                            return (
                                              <div className="product-major-usp-wrapper">
                                                <span className="product-card-usp-heading">Key benefit</span>
                                                {isLong ? (
                                                  <button
                                                    type="button"
                                                    className="product-major-usp product-usp"
                                                    onClick={(e) => handleUspClick(e, product.major_usp, 'Key benefit')}
                                                    title="View full"
                                                  >
                                                    <span className="product-major-usp-text">{majorUspPlain}</span>
                                                  </button>
                                                ) : (
                                                  <span className="product-major-usp-text">{majorUspPlain}</span>
                                                )}
                                              </div>
                                            );
                                          })()}
                                          {product.concerns_treated && product.concerns_treated.length > 0 && (
                                            <div className="product-info-section">
                                              <div className="product-info-label">
                                                <img
                                                  src={`${process.env.PUBLIC_URL}/icon/treat.png`}
                                                  alt="Treats"
                                                  className="product-info-icon"
                                                />
                                                <span>Treats</span>
                                              </div>
                                              <div className="product-tags-container">
                                                {product.concerns_treated.map((concern, i) => (
                                                  <span key={i} className="product-tag">{concern}</span>
                                                ))}
                                              </div>
                                            </div>
                                          )}

                                          {product.skin_goals_supported && product.skin_goals_supported.length > 0 && (
                                            <div className="product-info-section">
                                              <div className="product-info-label">
                                                <img
                                                  src={`${process.env.PUBLIC_URL}/icon/goal.png`}
                                                  alt="Goals"
                                                  className="product-info-icon"
                                                />
                                                <span>Goals</span>
                                              </div>
                                              <div className="product-tags-container">
                                                {product.skin_goals_supported.map((goal, i) => (
                                                  <span key={i} className="product-tag">{goal}</span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                        <div className="product-card-footer">
                                          {showProductPrice ? (
                                            <div className="product-price-quantity">
                                              {(() => {
                                                const unitNet = toNumberOrNull(product.amount) ?? 0;
                                                const unitSale = toNumberOrNull(product.sale_price) ?? unitNet;
                                                const totalNet = unitNet * qtyForPrice;
                                                const totalSale = unitSale * qtyForPrice;
                                                if (hasProductDiscount(product) && totalSale > 0 && totalSale < totalNet) {
                                                  return (
                                                    <>
                                                      <span className="product-price product-net-price-slash">{`₹${totalNet.toFixed(0)}`}</span>
                                                      <span className="product-price product-sale-price">{`₹${totalSale.toFixed(0)}`}</span>
                                                    </>
                                                  );
                                                }
                                                return (
                                                  <span className="product-price">{`₹${totalNet.toFixed(0)}`}</span>
                                                );
                                              })()}
                                            </div>
                                          ) : null}
                                          {showProductQuantity ? (
                                            <ProductCartCounter
                                              key={`counter-${productId}`}
                                              product={product}
                                              onAddToCart={(product, quantity) => handleAddToCart(product, quantity, productId)}
                                              disabled={!product.add_to_cart}
                                              initialQuantity={cartQuantity}
                                            />
                                          ) : (
                                            <button
                                              type="button"
                                              className={`skin-action-button add-to-cart-button ${cartQuantity > 0 ? 'request-selected' : ''}`}
                                              onClick={() => handleAddToCart(product, cartQuantity > 0 ? 0 : 1, productId)}
                                            >
                                              {cartQuantity > 0 ? 'Selected for Stock' : 'Add to Stock Request'}
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="no-products-message">
                                  <p>No evening products found.</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : null}
            </section>
          </div>
        </main>
      )}

      {/* USP Popup */}
      {uspPopup.open && (
        <div
          className="product-usp-popup-overlay"
          onClick={handleCloseUspPopup}
        >
          <div
            className="product-usp-popup"
            style={{
              top: `${uspPopup.position.top}px`,
              left: `${uspPopup.position.left}px`,
              width: `${Math.max(uspPopup.position.width, 300)}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="product-usp-popup-content">
              <button
                className="product-usp-popup-close"
                onClick={handleCloseUspPopup}
                aria-label="Close"
              >
                ×
              </button>
              {uspPopup.heading && <h3 className="product-usp-popup-heading">{uspPopup.heading}</h3>}
              {(() => {
                const raw = String(uspPopup.text);
                const isHtml = /<[a-z][\s\S]*>/i.test(raw);
                const html = isHtml ? sanitizeHtml(raw) : sanitizeHtml(raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br />'));
                return <div className="product-usp-popup-text" dangerouslySetInnerHTML={{ __html: html }} />;
              })()}
            </div>
          </div>
        </div>
      )}

      <PageFooter />

      {/* Checkout Modal (combined treatments + products) */}
      <CombinedCheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={handleCloseCheckoutModal}
        productCartItemsMap={cartItems}
        treatmentCartItemsMap={treatmentCartItems}
        onProductCartUpdate={handleCartUpdate}
        onTreatmentCartUpdate={handleTreatmentCartUpdate}
        sessionId={sessionId}
        apiBaseUrl={API_BASE_URL}
        token={token}
      />
    </div>
  );
};

export default ProductRecommendationsPage;

